import { AgentInfo, ChatLogEntryView } from "@/api/types";

import { slotsFromRaw } from "./a2aSlots";

/** A time interval in epoch-ms, carrying its ISO strings for rendering. */
export interface Interval {
  startMs: number;
  endMs: number;
  start: string;
  end: string;
}

/** One participant's offered availability. */
export interface TimelineLane {
  agentId: string;
  label: string;
  ranges: Interval[];
}

/** One day's slice of the timeline — a single card in the carousel. */
export interface DayTimeline {
  /** Local calendar day, e.g. "2026-07-02" (stable key + sort order). */
  dayKey: string;
  /** This day's lanes (every participant; empty track when unavailable that day). */
  lanes: TimelineLane[];
  /** The shared band(s) on this day. */
  overlap: Interval[];
  /** The booked slot, present only on the day it falls. */
  booked: Interval | null;
  /** Axis bounds (epoch-ms) for this day only. */
  axisStartMs: number;
  axisEndMs: number;
}

/** Everything needed to draw the overlap timeline for a negotiation. */
export interface TimelineModel {
  lanes: TimelineLane[];
  /** The band(s) free for every participant who has offered so far. */
  overlap: Interval[];
  /** The finally-booked slot (from an `accept`), if any. */
  booked: Interval | null;
  /** Axis bounds (epoch-ms) spanning every range + the booked slot. */
  axisStartMs: number;
  axisEndMs: number;
  /** One entry per calendar day that has any availability, ascending. */
  days: DayTimeline[];
}

function labelFor(agent: AgentInfo): string {
  if (agent.handle) return `@${agent.handle}`;
  if (agent.displayName) return agent.displayName;
  return agent.id.slice(0, 8);
}

function toInterval(s: { start: string; end: string }): Interval | null {
  const startMs = Date.parse(s.start);
  const endMs = Date.parse(s.end);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return null;
  return { startMs, endMs, start: s.start, end: s.end };
}

/** Sort + merge overlapping/touching intervals into a canonical ascending list. */
export function normalize(intervals: Interval[]): Interval[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
  const out: Interval[] = [];
  let cur = { ...sorted[0] };
  for (let i = 1; i < sorted.length; i++) {
    const r = sorted[i];
    if (r.startMs <= cur.endMs) {
      if (r.endMs > cur.endMs) {
        cur.endMs = r.endMs;
        cur.end = r.end;
      }
    } else {
      out.push(cur);
      cur = { ...r };
    }
  }
  out.push(cur);
  return out;
}

/** Intersect two interval sets — the sub-intervals present in both. */
function intersectTwo(a: Interval[], b: Interval[]): Interval[] {
  const na = normalize(a);
  const nb = normalize(b);
  const out: Interval[] = [];
  let i = 0;
  let j = 0;
  while (i < na.length && j < nb.length) {
    const startMs = Math.max(na[i].startMs, nb[j].startMs);
    const endMs = Math.min(na[i].endMs, nb[j].endMs);
    if (endMs > startMs) {
      out.push({
        startMs,
        endMs,
        start: new Date(startMs).toISOString(),
        end: new Date(endMs).toISOString(),
      });
    }
    if (na[i].endMs < nb[j].endMs) i++;
    else j++;
  }
  return out;
}

/** Local calendar-day key (YYYY-MM-DD) for an epoch-ms instant. */
function dayKeyOf(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Split a whole-negotiation model into one {@link DayTimeline} per calendar day
 * that has any availability — so the UI can show a card per day rather than one
 * giant multi-day axis. An interval is assigned to the day of its start; each
 * card's axis spans only that day's intervals. Every lane appears on every card
 * (empty track when that participant has nothing free that day) so rows align.
 */
function splitByDay(
  lanes: TimelineLane[],
  overlap: Interval[],
  booked: Interval | null,
): DayTimeline[] {
  const dayKeys = new Set<string>();
  lanes.forEach((l) => l.ranges.forEach((iv) => dayKeys.add(dayKeyOf(iv.startMs))));
  overlap.forEach((iv) => dayKeys.add(dayKeyOf(iv.startMs)));
  if (booked) dayKeys.add(dayKeyOf(booked.startMs));

  return [...dayKeys]
    .sort()
    .map((dayKey) => {
      const dayLanes = lanes.map((l) => ({
        ...l,
        ranges: l.ranges.filter((iv) => dayKeyOf(iv.startMs) === dayKey),
      }));
      const dayOverlap = overlap.filter((iv) => dayKeyOf(iv.startMs) === dayKey);
      const dayBooked = booked && dayKeyOf(booked.startMs) === dayKey ? booked : null;
      const dayIntervals: Interval[] = [
        ...dayLanes.flatMap((l) => l.ranges),
        ...dayOverlap,
        ...(dayBooked ? [dayBooked] : []),
      ];
      return {
        dayKey,
        lanes: dayLanes,
        overlap: dayOverlap,
        booked: dayBooked,
        axisStartMs: Math.min(...dayIntervals.map((i) => i.startMs)),
        axisEndMs: Math.max(...dayIntervals.map((i) => i.endMs)),
      };
    });
}

/** Intersect across every set — the bands free for all. */
export function intersectAll(sets: Interval[][]): Interval[] {
  if (sets.length === 0) return [];
  let acc = normalize(sets[0]);
  for (let k = 1; k < sets.length; k++) {
    acc = intersectTwo(acc, sets[k]);
    if (acc.length === 0) return [];
  }
  return acc;
}

/**
 * Build the timeline from a conversation's chat-log rows. Takes each agent's
 * LATEST offered availability (propose/counter), computes the overlap band the
 * same way the backend does, and picks up the booked slot from any `accept`.
 * Returns null when there's nothing worth drawing (no offered ranges yet).
 */
export function buildTimelineModel(rows: ChatLogEntryView[]): TimelineModel | null {
  const events = rows
    .filter((r): r is Extract<ChatLogEntryView, { kind: "A2A_EVENT" }> => r.kind === "A2A_EVENT")
    .slice()
    .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at));

  const laneByAgent = new Map<string, TimelineLane>();
  let booked: Interval | null = null;

  for (const e of events) {
    const { message_type, from, raw } = e.payload;
    const type = message_type.toLowerCase();
    if (type === "accept") {
      const slot = slotsFromRaw(raw)[0];
      const iv = slot ? toInterval(slot) : null;
      if (iv) booked = iv;
      continue;
    }
    if (type !== "propose_slots" && type !== "counter_propose") continue;
    if (!from) continue;
    const ranges = slotsFromRaw(raw)
      .map(toInterval)
      .filter((iv): iv is Interval => iv !== null);
    if (ranges.length === 0) continue;
    // Latest offer per agent wins.
    laneByAgent.set(from.id, { agentId: from.id, label: labelFor(from), ranges });
  }

  const lanes = [...laneByAgent.values()];
  if (lanes.length === 0 && !booked) return null;

  const overlap = lanes.length > 0 ? intersectAll(lanes.map((l) => l.ranges)) : [];

  const all: Interval[] = [
    ...lanes.flatMap((l) => l.ranges),
    ...overlap,
    ...(booked ? [booked] : []),
  ];
  if (all.length === 0) return null;
  const axisStartMs = Math.min(...all.map((i) => i.startMs));
  const axisEndMs = Math.max(...all.map((i) => i.endMs));
  const days = splitByDay(lanes, overlap, booked);

  return { lanes, overlap, booked, axisStartMs, axisEndMs, days };
}
